import { useCallback, useEffect, useMemo, useState } from 'react';
import * as userApi from '../../../api/user';
import * as patreonApi from '../../../api/patreon';
import { authBus } from './authBus';

type AuthProvider = 'subscriber' | 'patreon' | 'direct' | null;

type AuthState = {
  loading: boolean;
  isAuthenticated: boolean;
  provider: AuthProvider;
  username: string | null;
};

const initialState: AuthState = {
  loading: true,
  isAuthenticated: false,
  provider: null,
  username: null,
};

export function useAuthState() {
  const [state, setState] = useState<AuthState>(initialState);

  const refresh = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const sessionResponse = await userApi.session();
      if (sessionResponse.data?.isAuthenticated) {
        setState({
          loading: false,
          isAuthenticated: true,
          provider: 'direct',
          username: sessionResponse.data.user?.username ?? null,
        });
        return;
      }
    } catch {
      // fallback to Patreon auth check
    }

    try {
      const patreonResponse = await patreonApi.patreonIsAuth();
      if (patreonResponse.data?.isAuth) {
        setState({
          loading: false,
          isAuthenticated: true,
          provider: 'patreon',
          username: null,
        });
        return;
      }
    } catch {
      // ignore and fall through to unauthenticated state
    }

    setState({
      loading: false,
      isAuthenticated: false,
      provider: null,
      username: null,
    });
  }, []);

  useEffect(() => {
    void refresh();

    const unsubscribers = [
      authBus.on('login', () => void refresh()),
      authBus.on('logout', () => void refresh()),
      authBus.on('refresh', () => void refresh()),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [refresh]);

  const logout = useCallback(async () => {
    await userApi.logout();
    authBus.emit('logout');
  }, []);

  const providerLabel = useMemo(() => {
    switch (state.provider) {
      case 'patreon':
        return 'Patreon';
      case 'subscriber':
        return 'SubscribeStar';
      case 'direct':
        return 'Reader account';
      default:
        return null;
    }
  }, [state.provider]);

  return {
    ...state,
    providerLabel,
    refresh,
    logout,
  };
}
